import { ArrowLeftRight, CalendarRange, Minus } from 'lucide-react';
import { useFilters, type CompareMode } from '@/state/filters';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/cn';

interface CompareOption {
  value: CompareMode;
  label: string;
  hint: string;
  icon: React.ComponentType<{ className?: string }>;
}

const OPTIONS: CompareOption[] = [
  { value: 'previous-period', label: 'Prev', hint: 'Compare with previous period of equal length', icon: ArrowLeftRight },
  { value: 'previous-year', label: 'YoY', hint: 'Compare with the same window one year ago', icon: CalendarRange },
  { value: 'none', label: 'Off', hint: 'Hide deltas — show absolute numbers only', icon: Minus },
];

/**
 * Segmented control for the period-compare mode.
 *
 * Per Emil: hover/active are color-only (no movement); the press feedback
 * lives on `.press`. The active pill uses a layout-driven background so the
 * transition is the color shift rather than scaling the chip.
 */
export function CompareToggle() {
  const compareMode = useFilters((s) => s.compareMode);
  const setCompareMode = useFilters((s) => s.setCompareMode);

  return (
    <div
      role="radiogroup"
      aria-label="Comparison mode"
      className="inline-flex items-center gap-0.5 rounded-md bg-muted p-0.5"
    >
      {OPTIONS.map((opt) => {
        const active = compareMode === opt.value;
        const Icon = opt.icon;
        return (
          <Tooltip key={opt.value} content={opt.hint}>
            <button
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setCompareMode(opt.value)}
              className={cn(
                'press inline-flex items-center gap-1 rounded-sm px-2 py-1 text-[11px] font-medium uppercase tracking-wider transition-colors duration-150 ease-out focus-ring',
                active
                  ? 'bg-surface text-foreground shadow-soft'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon className="h-3 w-3" aria-hidden />
              {opt.label}
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
}
